import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Course {
  id: string;
  name: string;
  location: string;
  lat: number;
  lon: number;
  pricePerPlayer: number;
  rating: number;
}

interface MapViewProps {
  courses: Course[];
  onCourseClick: (course: Course) => void;
}

const customIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#2B8B3B" stroke="white" stroke-width="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
      <circle cx="12" cy="10" r="3" fill="white"></circle>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

export default function MapView({ courses, onCourseClick }: MapViewProps) {
  const coursesWithLocation = courses.filter(
    (course) => course.lat != null && course.lon != null
  );

  const center: [number, number] = coursesWithLocation.length > 0
    ? [
        coursesWithLocation.reduce((sum, c) => sum + c.lat, 0) / coursesWithLocation.length,
        coursesWithLocation.reduce((sum, c) => sum + c.lon, 0) / coursesWithLocation.length,
      ]
    : [37.7749, -122.4194];

  return (
    <div className="h-full rounded-lg overflow-hidden border-2 border-gray-200">
      {coursesWithLocation.length > 0 ? (
        <MapContainer
          center={center}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {coursesWithLocation.map((course) => (
            <Marker
              key={course.id}
              position={[course.lat, course.lon]}
              icon={customIcon}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-semibold text-[#2B8B3B]">{course.name}</h3>
                  <p className="text-sm text-gray-600">{course.location}</p>
                  <p className="text-sm font-medium mt-1">
                    ${course.pricePerPlayer.toFixed(2)}/player
                  </p>
                  <button
                    onClick={() => onCourseClick(course)}
                    className="mt-2 w-full text-sm bg-[#2B8B3B] text-white px-3 py-1 rounded hover:bg-[#236B2F] transition-colors"
                  >
                    Book Now
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      ) : (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <p className="text-gray-500">No courses found matching your search</p>
        </div>
      )}
    </div>
  );
}
